import polygonClipping from 'polygon-clipping';

// All geometry is in SVG coordinates: x right, y down. The physical LEFT
// normal of a swipe is (dy, -dx), so left has a NEGATIVE cross product.
export function signedArea(ring) {
  let area = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    area += a[0] * b[1] - b[0] * a[1];
  }
  return area / 2;
}
export function multiArea(multi) {
  return multi.reduce((sum, polygon) => sum + Math.abs(signedArea(polygon[0])) - polygon.slice(1).reduce((holes, ring) => holes + Math.abs(signedArea(ring)), 0), 0);
}
const cross = (a, b, c) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
const validity = new WeakMap();
export function isSimplePolygon(points) {
  if (!Array.isArray(points) || points.length < 3 || !points.every(p => Array.isArray(p) && p.length === 2 && p.every(Number.isFinite))) return false;
  const ring = points.length > 3 && points[0][0] === points.at(-1)[0] && points[0][1] === points.at(-1)[1] ? points.slice(0, -1) : points;
  if (Math.abs(signedArea(ring)) < 1e-9) return false;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    for (let j = i + 2; j < ring.length; j++) {
      if (i === 0 && j === ring.length - 1) continue;
      const c = ring[j], d = ring[(j + 1) % ring.length];
      const c1 = cross(a, b, c), c2 = cross(a, b, d), c3 = cross(c, d, a), c4 = cross(c, d, b);
      if (c1 * c2 < 0 && c3 * c4 < 0) return false;
    }
  }
  return true;
}
export function splitPolygon(points, start, end) {
  const invalid = { valid: false, reason: 'Cut all the way through the shape' };
  if (!Array.isArray(points) || ![start, end].every(p => Array.isArray(p) && p.length === 2 && p.every(Number.isFinite))) return invalid;
  if (!validity.has(points)) validity.set(points, isSimplePolygon(points));
  if (!validity.get(points)) return invalid;
  const dx = end[0] - start[0], dy = end[1] - start[1], length = Math.hypot(dx, dy);
  if (length <= 1e-7) return invalid;
  const direction = [dx / length, dy / length], normal = [dy / length, -dx / length];
  // Size half-planes relative to both polygon and swipe (not a hardcoded canvas).
  const radius = Math.max(...points.map(p => Math.hypot(p[0] - start[0], p[1] - start[1])), 1) * 4 + 1;
  const a = [start[0] - direction[0] * radius, start[1] - direction[1] * radius];
  const b = [start[0] + direction[0] * radius, start[1] + direction[1] * radius];
  const halfPlane = sign => [[a, b, [b[0] + normal[0] * radius * sign, b[1] + normal[1] * radius * sign], [a[0] + normal[0] * radius * sign, a[1] + normal[1] * radius * sign]]];
  try {
    const selected = polygonClipping.intersection([points], halfPlane(1));
    const remainder = polygonClipping.intersection([points], halfPlane(-1));
    const total = Math.abs(signedArea(points)), selectedArea = multiArea(selected), remainderArea = multiArea(remainder);
    // Reject tangencies and numerically degenerate fragments, not genuine tiny cuts.
    const epsilon = Math.max(total * 1e-10, 1e-10);
    if (selectedArea <= epsilon || remainderArea <= epsilon || Math.abs(selectedArea + remainderArea - total) > total * 1e-7) return invalid;
    return { valid: true, selected, remainder, selectedArea, remainderArea, total, percent: 100 * selectedArea / total, normal, start, end };
  } catch {
    return invalid;
  }
}
export function polygonPath(points) {
  return `M${points.map(p => p.join(',')).join('L')}Z`;
}
export function multiPath(multi) {
  return multi.map(poly => poly.map(polygonPath).join(' ')).join(' ');
}
export function seededRandom(seed) {
  let state = seed >>> 0;
  return () => {
    state += 0x6D2B79F5;
    let t = state;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
export function hashSeed(value) {
  let h = 2166136261;
  for (const char of String(value)) h = Math.imul(h ^ char.charCodeAt(0), 16777619);
  return h >>> 0;
}
// Generation is separate from the unchanged slicing math above.
export { generateShape, createChallenge } from './shapes.mjs';
